import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  evaluatePackageAdmission,
  getBatch,
  getBatchExceptions,
  getPackageAdmission,
  getProgram,
  listPortfolioLoans,
} from '../../api/originatorApi';
import { formatApiFailure, isStaleAdmissionError, STALE_ADMISSION_MESSAGE } from '../../api/apiError';
import { isReadOnlySession } from '../../auth/access';
import { readDemoSession } from '../../auth/demoSession';
import { LoanTapeCard } from '../../components/package/LoanTapeCard';
import {
  PackageAssetsTable,
  mapOriginatorLoanToAssetRow,
} from '../../components/package/PackageAssetsTable';
import { PackageViewSwitch } from '../../components/package/PackageViewSwitch';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button } from '../../components/ui/Button';
import { LifecycleInfoPopover } from '../../components/ui/LifecycleInfoPopover';
import { CureExceptionModal } from '../../components/exceptions/CureExceptionModal';
import { PackageAdmissionOverview } from '../../components/admission/PackageAdmissionOverview';
import { AdmissionDetailsDrawer } from '../../components/admission/AdmissionDetailsDrawer';
import { PackageCertificationModal } from '../../components/admission/PackageCertificationModal';
import { formatRelativeTime } from '../../utils/format';
import { originatorNextAction } from './originatorPackageLabels';
import { useAdmissionPanelSearch } from '../../admission/useAdmissionPanelSearch';
import { waitForAdmissionEvaluation, admissionPollConfig } from '../../admission/pollAdmission';
import {
  admissionNeedsOriginator,
  canCertifyAdmission,
  isEvaluatingAdmission,
} from '../../admission/selectors';
import { ADMISSION_READINESS } from '../../admission/labels';

export { originatorNextAction };

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function packageStatusLabel(status) {
  if (status === 'CERTIFIED') return 'Certified';
  return status;
}

/**
 * Lifecycle completion uses only Originator batch fields. Provider ReviewTask
 * status is not on GET /v1/batches/{id}, so CERTIFIED does not complete
 * Provider/Custodian/admission steps.
 */
export function originatorLifecycleSteps(batch) {
  const certified = batch.package_status === 'CERTIFIED';
  const authorityApproved = batch.authority_status === 'APPROVED';
  const admitted = batch.custody_status === 'ACCEPTED';

  return [
    {
      label: 'Received',
      shortLabel: 'Received',
      complete: true,
      detail: formatRelativeTime(batch.created_at),
    },
    {
      label: 'Prevalidated',
      shortLabel: 'Prevalidated',
      complete: batch.upload_status === 'COMPLETE',
      detail: batch.upload_status === 'COMPLETE' ? 'Complete' : undefined,
    },
    {
      label: 'Classified / Certified',
      shortLabel: 'Certified',
      complete: certified,
      detail: certified ? 'Certified' : undefined,
    },
    {
      label: 'Provider Review',
      shortLabel: 'Provider',
      complete: authorityApproved || admitted,
      detail: authorityApproved || admitted ? 'Complete' : undefined,
    },
    {
      label: 'Custodian Review',
      shortLabel: 'Custodian',
      complete: authorityApproved || admitted,
      detail: authorityApproved || admitted ? 'Complete' : undefined,
    },
    {
      label: 'Authority Approved',
      shortLabel: 'Authority',
      complete: authorityApproved,
      detail: authorityApproved ? 'Approved' : undefined,
    },
    {
      label: 'Admitted / Authoritative',
      shortLabel: 'Admitted',
      complete: admitted,
      detail: admitted ? 'Accepted' : undefined,
    },
  ];
}

function Fact({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-eorig-border py-2 last:border-b-0">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-eorig-muted">
        {label}
      </span>
      <span className="min-w-0 truncate text-right text-sm text-eorig-ink">
        {children}
      </span>
    </div>
  );
}

function PackageFacts({ batch, program, admission }) {
  const nextAction = originatorNextAction(batch, admission);
  return (
    <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
      <h2 className="text-sm font-semibold text-eorig-ink">Package facts</h2>
      <div className="mt-2">
        <Fact label="Package ID">
          <span className="font-mono text-[12px]">{batch.id}</span>
        </Fact>
        {batch.reference_number ? (
          <Fact label="Reference">{batch.reference_number}</Fact>
        ) : null}
        {program?.name ? <Fact label="Program">{program.name}</Fact> : null}
        <Fact label="Status">
          <StatusPill
            value={batch.package_status}
            label={packageStatusLabel(batch.package_status)}
          />
        </Fact>
        <Fact label="Next action">
          <StatusPill
            value={nextAction.label}
            tone={nextAction.tone}
            label={nextAction.label}
          />
        </Fact>
        <Fact label="Upload">{batch.upload_status || '—'}</Fact>
        <Fact label="Received">{formatDate(batch.created_at)}</Fact>
        <Fact label="Updated">{formatDate(batch.updated_at)}</Fact>
      </div>
    </section>
  );
}

function recordCertification(current, result) {
  if (!result) return current;
  if (result.replayed && current?.id && current.id === result.id) return current;
  return result;
}

export function PackageDetailPage() {
  const { packageId } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const admissionPanel = useAdmissionPanelSearch();
  const viewParam = searchParams.get('view');
  const view = viewParam === 'assets' ? 'assets' : 'audit';

  const [batch, setBatch] = useState(null);
  const [program, setProgram] = useState(null);
  const [exceptions, setExceptions] = useState([]);
  const [admission, setAdmission] = useState(null);
  const [assetRows, setAssetRows] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [certifyOpen, setCertifyOpen] = useState(false);
  const [factsSaving, setFactsSaving] = useState(false);
  const [certificationReceipt, setCertificationReceipt] = useState(
    () => location.state?.certification ?? null
  );
  const [cureTarget, setCureTarget] = useState(null);

  function setView(nextView) {
    const next = new URLSearchParams(searchParams);
    if (nextView === 'assets') {
      next.set('view', 'assets');
    } else {
      next.delete('view');
    }
    setSearchParams(next, { replace: true });
  }

  const openExceptionCount = useMemo(
    () => exceptions.filter((item) => item.status !== 'CURED').length,
    [exceptions]
  );

  async function reloadAdmission() {
    const admissionResponse = await getPackageAdmission(packageId);
    setAdmission(admissionResponse);
    return admissionResponse;
  }

  async function reload() {
    const batchResponse = await getBatch(packageId);
    const [exceptionsResponse, programResponse, admissionResponse] = await Promise.all([
      getBatchExceptions(packageId),
      getProgram(batchResponse.program_id).catch(() => null),
      getPackageAdmission(packageId).catch(() => null),
    ]);
    setBatch(batchResponse);
    setExceptions(exceptionsResponse || []);
    setProgram(programResponse);
    setAdmission(admissionResponse);
    return admissionResponse;
  }

  function applySavedAdmission(next) {
    if (!next) return;
    setAdmission(next);
    if (Array.isArray(next.exceptions)) {
      setExceptions(next.exceptions);
    }
    setCertifyOpen(false);
  }

  async function reevaluateAdmission(rowVersion) {
    setBusyAction('evaluate');
    try {
      setAdmission((current) =>
        current
          ? { ...current, readiness_status: ADMISSION_READINESS.EVALUATING }
          : { package_id: packageId, readiness_status: ADMISSION_READINESS.EVALUATING }
      );
      const version = rowVersion !== undefined && rowVersion !== null ? rowVersion : admission?.row_version;
      const started = await evaluatePackageAdmission(packageId, {
        rowVersion: version,
      });
      if (
        started.readiness_status === ADMISSION_READINESS.EVALUATING ||
        started.admission?.readiness_status === ADMISSION_READINESS.EVALUATING
      ) {
        const settled = await waitForAdmissionEvaluation(
          () => getPackageAdmission(packageId),
          admissionPollConfig
        );
        setAdmission(settled);
        return settled;
      }
      const latest = started.admission || (await getPackageAdmission(packageId));
      setAdmission(latest);
      return latest;
    } catch (requestError) {
      if (isStaleAdmissionError(requestError)) {
        setError(STALE_ADMISSION_MESSAGE);
        return reloadAdmission();
      }
      throw requestError;
    } finally {
      setBusyAction('');
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function loadBatchData() {
      if (!packageId) return;
      setLoading(true);
      setError('');
      try {
        const batchResponse = await getBatch(packageId);
        const [exceptionsResponse, programResponse, admissionResponse] = await Promise.all([
          getBatchExceptions(packageId),
          getProgram(batchResponse.program_id).catch(() => null),
          getPackageAdmission(packageId).catch(() => null),
        ]);
        if (cancelled) return;
        setBatch(batchResponse);
        setExceptions(exceptionsResponse || []);
        setProgram(programResponse);
        setAdmission(admissionResponse);
      } catch (requestError) {
        if (cancelled) return;
        setError(requestError.message || 'Unable to load package details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadBatchData();
    return () => {
      cancelled = true;
    };
  }, [packageId]);

  useEffect(() => {
    if (view !== 'assets' || !batch) return undefined;
    let cancelled = false;

    async function loadAssets() {
      setAssetsLoading(true);
      setAssetsError('');
      try {
        const response = await listPortfolioLoans({
          page: 1,
          pageSize: 100,
          batchId: packageId,
        });
        if (cancelled) return;
        setAssetRows((response.items || []).map(mapOriginatorLoanToAssetRow));
      } catch (requestError) {
        if (cancelled) return;
        setAssetRows([]);
        setAssetsError(requestError.message || 'Unable to load package assets.');
      } finally {
        if (!cancelled) setAssetsLoading(false);
      }
    }

    loadAssets();
    return () => {
      cancelled = true;
    };
  }, [view, batch, packageId]);

  async function handleCertify() {
    if (!batch) return;
    if (batch.package_status === 'CERTIFIED' && !admissionNeedsOriginator(admission)) return;
    setBusyAction('certify');
    setError('');
    try {
      const latest = await reloadAdmission();
      if (isEvaluatingAdmission(latest)) {
        setError('Processing admission checks... Certification is unavailable until evaluation completes.');
        return;
      }
      if (!canCertifyAdmission(latest)) {
        setError(
          'This package is not ready to certify. Confirm admission details or cure admission exceptions first.'
        );
        if (latest?.readiness_status === ADMISSION_READINESS.NOT_EVALUATED) {
          await reevaluateAdmission(latest.row_version);
        }
        return;
      }
      setCertifyOpen(true);
    } catch (requestError) {
      setError(formatApiFailure(requestError, 'Unable to load admission.'));
    } finally {
      setBusyAction('');
    }
  }

  const exceptionColumns = useMemo(
    () => [
      {
        key: 'code',
        header: 'Code',
        render: (row) => (
          <Link
            to={`/exceptions/${row.id}`}
            state={{ exception: { ...row, package_id: row.package_id || packageId } }}
            className="font-mono text-[12px] font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
          >
            {row.code}
          </Link>
        ),
      },
      {
        key: 'description',
        header: 'Description',
        className: 'max-w-[20rem]',
        render: (row) => (
          <span className="text-eorig-ink/80">{row.description || '—'}</span>
        ),
      },
      {
        key: 'severity',
        header: 'Severity',
        render: (row) => <StatusPill value={row.severity} />,
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => <StatusPill value={row.status} />,
      },
      {
        key: 'assigned_to',
        header: 'Assigned to',
        render: (row) => (
          <span className="text-eorig-ink/80">{row.assigned_to || 'Unassigned'}</span>
        ),
      },
      {
        key: 'context',
        header: 'Admission context',
        render: (row) => (
          <span className="text-[12px] text-eorig-muted">
            {row.fact_code ? `Fact ${row.fact_code}` : '—'}
            {row.document_id ? ` · Doc ${String(row.document_id).slice(0, 8)}` : ''}
          </span>
        ),
      },
      {
        key: 'updated_at',
        header: 'Updated',
        render: (row) => (
          <span className="text-eorig-muted">{formatDate(row.updated_at)}</span>
        ),
      },
      {
        key: 'action',
        header: '',
        align: 'right',
        render: (row) => (
          <Button
            variant="ghost"
            className="px-3 py-1 text-xs"
            disabled={row.status === 'CURED'}
            onClick={() => setCureTarget(row)}
          >
            {row.status === 'CURED' ? 'Cured' : 'Cure'}
          </Button>
        ),
      },
    ],
    [packageId]
  );

  if (loading) {
    return (
      <div className="rounded-2xl border border-eorig-border bg-white p-6 text-sm text-eorig-muted shadow-eorig-card">
        Loading package details…
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="space-y-4">
        <div className="rounded-2xl border border-eorig-border bg-white p-6 text-sm text-eorig-muted shadow-eorig-card">
          {error || 'Package not found.'}
        </div>
        <Link
          to="/packages"
          className="text-sm font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
        >
          ← Back to packages
        </Link>
      </div>
    );
  }

  const showCertifyAction =
    batch.package_status !== 'CERTIFIED' || admissionNeedsOriginator(admission);
  const nextAction = originatorNextAction(batch, admission);
  const certifyEnabled =
    showCertifyAction &&
    canCertifyAdmission(admission) &&
    !isEvaluatingAdmission(admission) &&
    !factsSaving;
  const certifyDisabledReason = isEvaluatingAdmission(admission)
    ? 'Processing admission checks...'
    : !canCertifyAdmission(admission)
      ? 'Certification is unavailable until admission readiness is ready for Provider review.'
      : undefined;

  return (
    <div className="space-y-5">
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {certificationReceipt ? (
        <section
          aria-label="Certification receipt"
          className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card"
        >
          <h2 className="text-sm font-semibold text-eorig-ink">Certification recorded</h2>
          <p className="mt-2 text-sm text-eorig-ink">
            Version {certificationReceipt.version ?? '—'}
            {certificationReceipt.supersedes_id
              ? ` · Supersedes ${certificationReceipt.supersedes_id}`
              : ''}
          </p>
          {certificationReceipt.certification_hash ? (
            <p className="mt-1 break-all font-mono text-[12px] text-eorig-ink">
              {certificationReceipt.certification_hash}
            </p>
          ) : null}
          <p className="mt-2 text-[13px] text-eorig-ink">Provider review is next.</p>
          {batch.authority_status !== 'APPROVED' ? (
            <p className="text-[13px] text-eorig-ink">Authority remains pending.</p>
          ) : null}
          {certificationReceipt.replayed ? (
            <p className="mt-1 text-[12px] text-eorig-muted">This matches the existing certification.</p>
          ) : null}
        </section>
      ) : null}

      <div className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
        <LifecycleInfoPopover steps={originatorLifecycleSteps(batch)}>
          {(lifecycleTrigger) => (
            <>
              <nav className="flex items-center gap-1.5 text-[12px] text-eorig-muted" aria-label="Breadcrumb">
                <Link to="/packages" className="font-semibold text-eorig-navy-600 hover:text-eorig-cyan">
                  Packages
                </Link>
                <span aria-hidden>›</span>
                <span className="min-w-0 truncate font-mono text-eorig-ink">
                  {batch.reference_number || batch.id}
                </span>
              </nav>

              <div className="mt-2 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex items-start gap-2">
                    <h1 className="min-w-0 text-lg font-semibold tracking-tight text-eorig-ink break-words">
                      {program?.name ? `${program.name} · ` : ''}Package{' '}
                      {batch.reference_number || batch.id}
                    </h1>
                    {lifecycleTrigger}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <StatusPill
                      value={batch.package_status}
                      label={packageStatusLabel(batch.package_status)}
                    />
                    <StatusPill
                      value={nextAction.label}
                      tone={nextAction.tone}
                      label={nextAction.label}
                    />
                    <span className="text-[12px] text-eorig-muted">
                      Upload {batch.upload_status} · Open exceptions {openExceptionCount}
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap items-center gap-2 lg:justify-end">
                  {showCertifyAction ? (
                    <Button
                      variant="gradient"
                      onClick={handleCertify}
                      disabled={!certifyEnabled || Boolean(busyAction)}
                      aria-disabled={!certifyEnabled || Boolean(busyAction)}
                      title={certifyDisabledReason}
                    >
                      {isEvaluatingAdmission(admission)
                        ? 'Processing…'
                        : 'Certify package'}
                    </Button>
                  ) : (
                    <StatusPill value="CERTIFIED" label="Certified ✓" />
                  )}
                </div>
              </div>
            </>
          )}
        </LifecycleInfoPopover>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <PackageViewSwitch value={view} onChange={setView} />
        <span className="text-[11px] text-eorig-muted">
          {view === 'audit'
            ? 'Package audit detail'
            : 'Open a loan to review documents and extracted fields'}
        </span>
      </div>

      {view === 'audit' ? (
        <>
          <PackageAdmissionOverview
            admission={admission}
            exceptions={exceptions}
            onOpenDocument={(documentId) => admissionPanel.openPanel(documentId)}
          />

          <div className="grid gap-4 lg:grid-cols-3 lg:items-start">
            <div className="min-w-0 space-y-4 lg:col-span-1">
              <PackageFacts batch={batch} program={program} admission={admission} />
              <LoanTapeCard packageId={batch.id} canManage={!isReadOnlySession(readDemoSession())} />
            </div>

            <div className="min-w-0 space-y-2 lg:col-span-2">
              <h2 className="text-sm font-semibold text-eorig-ink">
                Batch-level exceptions
              </h2>
              <DataTable
                columns={exceptionColumns}
                rows={exceptions}
                rowKey={(row) => row.id}
                loading={false}
                emptyMessage="No exceptions found for this package."
                minWidth={820}
              />
            </div>
          </div>
        </>
      ) : (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-eorig-ink">Loans</h2>
          {assetsError ? (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {assetsError}
            </div>
          ) : null}
          <PackageAssetsTable
            rows={assetRows}
            loading={assetsLoading}
            loanHref={(row) => `/packages/${packageId}/assets/${row.id}`}
            emptyMessage="No loans linked to this package yet."
          />
        </section>
      )}

      <Link
        to="/packages"
        className="inline-flex text-sm font-semibold text-eorig-navy-600 hover:text-eorig-cyan"
      >
        ← Back to packages
      </Link>

      <AdmissionDetailsDrawer
        open={admissionPanel.open}
        onClose={admissionPanel.closePanel}
        documentId={admissionPanel.documentId}
        packageId={batch.id}
        packageAdmission={admission}
        factsReadOnly={!admissionNeedsOriginator(admission) && batch.package_status === 'CERTIFIED'}
        onAdmissionUpdated={applySavedAdmission}
        onFactsActivityChange={setFactsSaving}
      />

      <PackageCertificationModal
        open={certifyOpen}
        onClose={() => setCertifyOpen(false)}
        packageId={batch.id}
        admission={admission}
        profile={program?.admission_profile}
        onAdmissionUpdated={applySavedAdmission}
        onCertified={async (result) => {
          setCertifyOpen(false);
          setCertificationReceipt((current) => recordCertification(current, result));
          if (result?.package_status) {
            setBatch((current) =>
              current ? { ...current, package_status: result.package_status } : current
            );
          }
          try {
            await reload();
          } catch (requestError) {
            setError(formatApiFailure(requestError, 'Unable to refresh package.'));
          }
        }}
        onStale={async (requestError, latest) => {
          setCertifyOpen(false);
          setError(formatApiFailure(requestError));
          if (latest) setAdmission(latest);
          if (latest && !canCertifyAdmission(latest)) {
            try {
              await reevaluateAdmission(latest.row_version);
            } catch (refreshError) {
              setError(formatApiFailure(refreshError, 'Unable to refresh admission.'));
            }
          }
        }}
      />

      <CureExceptionModal
        exception={cureTarget}
        onClose={() => setCureTarget(null)}
        onCured={async () => {
          setCureTarget(null);
          try {
            await reload();
            await reevaluateAdmission();
          } catch (requestError) {
            setError(requestError.message || 'Unable to refresh admission after cure.');
          }
        }}
      />
    </div>
  );
}
